import { select } from 'd3-selection';
import { geoMercator, geoPath } from 'd3-geo';

// CNN disputed-borders basemap, Natural Earth 10m edited to CNN guidelines and
// clipped to the Gulf. Drawn in the set's README stacking order — polys (fill
// only), country lines, then disputed lines dotted on top.
import borders from './data/cnn-borders-gulf.json';
import tracks from './data/tracks.json';
import MAP from './mapConfig';
import './styles/graphic.scss';

const {
  ASPECT, BBOX, CALLOUTS, chevrons, clipToFrame, COLORS, endAngle, fitTarget,
  FONTS, LABELS, listFor, SEGMENTS, SHADOW, TRACK_LABELS,
} = MAP;

function polyline(pts) {
  if (pts.length < 2) return null;
  return `M${pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join('L')}`;
}

// `font` is one of the FONTS entries, which also supplies the line height for
// multi-line labels.
function haloText(g, x, y, txt, cls, font, anchor) {
  const t = g.append('text')
    .attr('x', x)
    .attr('y', y)
    .attr('text-anchor', anchor || 'middle')
    .attr('class', cls)
    .attr('font-size', font.size)
    .attr('letter-spacing', font.track ? `${font.track}em` : null)
    .attr('stroke', COLORS.halo)
    .attr('stroke-width', 3.5)
    .attr('paint-order', 'stroke');
  if (Array.isArray(txt)) {
    txt.forEach((ln, i) => t.append('tspan').attr('x', x).attr('dy', i ? font.lh : 0).text(ln));
  } else {
    t.text(txt);
  }
  return t;
}

function drawBasemap(svg, path) {
  // Coastal drop shadow, the standard cartographic cue for a land/water edge.
  // One filter on the whole MultiPolygon, so the shadow follows the combined
  // silhouette instead of stacking where islands and countries abut.
  svg.append('defs')
    .append('filter')
    .attr('id', 'k-land-shadow')
    .attr('x', '-5%')
    .attr('y', '-5%')
    .attr('width', '110%')
    .attr('height', '110%')
    .append('feDropShadow')
    .attr('dx', SHADOW.dx)
    .attr('dy', SHADOW.dy)
    .attr('stdDeviation', SHADOW.blur)
    .attr('flood-color', COLORS.shadow)
    .attr('flood-opacity', 1);

  svg.append('path').datum(borders.polys).attr('d', path)
    .attr('fill', COLORS.land)
    .attr('filter', 'url(#k-land-shadow)');
  svg.append('path').datum(borders.lines).attr('d', path)
    .attr('fill', 'none')
    .attr('stroke', COLORS.border)
    .attr('stroke-width', 0.9);
  if (borders.disputed && borders.disputed.coordinates.length) {
    svg.append('path').datum(borders.disputed).attr('d', path)
      .attr('fill', 'none')
      .attr('stroke', COLORS.disputed)
      .attr('stroke-width', 0.9)
      .attr('stroke-linecap', 'round')
      .attr('stroke-dasharray', '0.1,2.6');
  }
}

function drawLabels(svg, proj, bp) {
  const g = svg.append('g').attr('class', 'k-labels');
  listFor(LABELS.water, bp).forEach((d) => {
    const p = proj(d.c);
    if (p) haloText(g, p[0], p[1], d.n, 'k-lbl-water', FONTS.water);
  });
  listFor(LABELS.countries, bp).forEach((d) => {
    const p = proj(d.c);
    if (p) haloText(g, p[0], p[1], d.n, 'k-lbl-country', FONTS.country);
  });
  listFor(LABELS.places, bp).forEach((d) => {
    const p = proj(d.c);
    if (!p) return;
    g.append('circle').attr('cx', p[0]).attr('cy', p[1]).attr('r', 2.4)
      .attr('fill', '#fff')
      .attr('stroke', '#8b9aa4')
      .attr('stroke-width', 1);
    haloText(g, p[0] + (d.dx || 0), p[1] + (d.dy == null ? 4 : d.dy), d.n,
      'k-lbl-place', FONTS.place, d.anchor);
  });
}

// Draws every track segment and returns where the clipped ones left the frame,
// so a callout can be pinned to that spot.
function drawSegments(svg, proj, W, H, bp) {
  const host = svg.append('g').attr('class', 'k-tracks');
  const exits = {};
  SEGMENTS.forEach((seg) => {
    let pts = tracks[seg.track[0]][seg.track[1]].map(c => proj(c)).filter(Boolean);
    let cutTail = false;
    if (seg.clip) {
      const cut = clipToFrame(pts, W, H);
      pts = cut.pts;
      cutTail = cut.cutTail;
      if (cutTail) exits[seg.key] = pts[pts.length - 1];
    }
    if (pts.length < 2) return;
    const g = host.append('g');
    g.append('path')
      .attr('d', polyline(pts))
      .attr('fill', 'none')
      .attr('stroke', seg.color)
      .attr('stroke-width', seg.dash ? 2.2 : 2.4)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('stroke-dasharray', seg.dash);
    if (seg.marks) {
      const marks = g.append('g');
      chevrons(pts, bp === 'narrow' ? 34 : 46).forEach((c) => {
        marks.append('path')
          .attr('d', 'M-2.6,-2.9 L2.9,0 L-2.6,2.9 Z')
          .attr('fill', seg.color)
          .attr('transform', `translate(${c.x.toFixed(1)},${c.y.toFixed(1)}) rotate(${c.angle.toFixed(1)})`);
      });
    }

    // A bigger arrowhead where a track ends, whether that is the last position
    // broadcast or the point where a clipped track runs off the map.
    if (seg.arrow && pts.length > 1) {
      const b = pts[pts.length - 1];
      const ang = endAngle(pts, 12);
      g.append('path')
        .attr('d', 'M-7,-5 L2,0 L-7,5 Z')
        .attr('fill', seg.color)
        .attr('transform', `translate(${b[0].toFixed(1)},${b[1].toFixed(1)}) rotate(${ang.toFixed(1)})`);
    }
  });
  return exits;
}

function drawTrackLabels(svg, proj, bp) {
  const g = svg.append('g').attr('class', 'k-track-labels');
  listFor(TRACK_LABELS, bp).forEach((d) => {
    const p = proj(d.c);
    if (p) {
      haloText(g, p[0], p[1], d.n, 'k-lbl-track', FONTS.vessel, d.anchor)
        .attr('fill', COLORS[d.color]);
    }
  });
}

export default function drawGraphic(props, container) {
  const W = Math.max(280, Math.round(props.initialWidth));
  const bp = W <= 500 ? 'narrow' : 'wide';
  const H = Math.round(W * ASPECT[bp]);
  const pad = bp === 'narrow' ? 6 : 10;

  const root = select(container.node ? container.node() : container);
  const wrap = root.append('div').attr('class', 'kiku');

  // --- legend ---------------------------------------------------------------
  // Mobile only. At this width neither track has clear water beside it to take
  // a name, so the two are keyed above the map; on wide they are labelled in
  // place and no key is needed.
  if (bp === 'narrow') {
    const legend = wrap.append('div').attr('class', 'kiku__legend');
    [['kiku', 'Kiku'], ['nave', 'Nave Electron']].forEach(([ship, label]) => {
      const item = legend.append('span').attr('class', 'kiku__leg');
      item.append('svg')
        .attr('width', 22)
        .attr('height', 8)
        .attr('aria-hidden', 'true')
        .append('line')
        .attr('x1', 1)
        .attr('y1', 4)
        .attr('x2', 21)
        .attr('y2', 4)
        .attr('stroke', COLORS[ship])
        .attr('stroke-width', 2.4)
        .attr('stroke-linecap', 'round');
      item.append('span').text(label);
    });
  }

  // --- map ------------------------------------------------------------------
  const proj = geoMercator()
    .precision(0.2)
    .fitExtent([[pad, pad], [W - pad, H - pad]], fitTarget(BBOX[bp]));

  const svg = wrap.append('svg')
    .attr('class', 'k-map')
    .attr('viewBox', `0 0 ${W} ${H}`)
    .attr('width', W)
    .attr('height', H)
    .attr('role', 'img')
    .attr('aria-label', 'Map of the Persian Gulf and Gulf of Oman tracing the '
      + 'tanker Kiku from Qatar through the Strait of Hormuz to a ship-to-ship '
      + 'transfer off Fujairah');

  svg.append('rect').attr('width', W).attr('height', H).attr('fill', COLORS.water);
  drawBasemap(svg, geoPath(proj));
  drawLabels(svg, proj, bp);

  const exits = drawSegments(svg, proj, W, H, bp);
  drawTrackLabels(svg, proj, bp);

  // --- dated callouts -------------------------------------------------------
  const calloutHost = svg.append('g').attr('class', 'k-callouts');
  listFor(CALLOUTS, bp).forEach((c) => {
    // naveOut rides on the clipped track's exit point rather than a fixed
    // coordinate, so it stays put if the frame or breakpoint changes.
    const p = c.key === 'naveOut' ? exits.away : proj(c.at);
    if (!p) return;
    // The date's color, the Kiku's red unless the callout names another. The
    // description below it is ink in every callout.
    const color = c.color ? COLORS[c.color] : COLORS.kiku;
    const g = calloutHost.append('g');
    const tx = p[0] + c.dx;
    const ty = p[1] + c.dy;

    if (c.leader) {
      g.append('path')
        .attr('d', `M${p[0]},${p[1]} L${tx},${c.dy < 0 ? ty + 8 : ty - 10}`)
        .attr('fill', 'none').attr('stroke', COLORS.leader)
        .attr('stroke-width', 0.8);
    }
    if (c.dot) {
      g.append('circle').attr('cx', p[0]).attr('cy', p[1]).attr('r', 4.6)
        .attr('fill', COLORS[c.dot])
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.6);
    }

    const t = g.append('text')
      .attr('x', tx)
      .attr('y', ty)
      .attr('text-anchor', c.anchor)
      .attr('stroke', COLORS.halo)
      .attr('stroke-width', 3.5)
      .attr('paint-order', 'stroke');
    t.append('tspan').attr('class', 'k-cal-date').attr('x', tx)
      .attr('font-size', FONTS.date.size)
      .attr('fill', color)
      .text(c.date);
    (Array.isArray(c.text) ? c.text : [c.text]).forEach((ln, i) => {
      t.append('tspan').attr('class', 'k-cal-text').attr('x', tx)
        .attr('font-size', FONTS.text.size)
        .attr('dy', i ? FONTS.text.lh : FONTS.date.lh)
        .attr('fill', COLORS.ink)
        .text(ln);
    });
  });
}
